/* WhiteboardCanvas — pure SVG surface that renders strokes + elements.
 *
 * Handles: infinite-canvas viewBox, freehand path drawing, element drag/select,
 * click-drag shape creation. Emits high-level intents to the parent.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import MathText from "@/components/MathText";
import { renderProp } from "./InteractiveProps";
import { CANVAS_W, CANVAS_H, clientToWorld } from "./wbUtils";

export default function WhiteboardCanvas({
  view, setView,
  elements, setElements,
  strokes,
  selectedId, setSelectedId,
  editing, setEditing,
  tool, color, strokeWidth,
  onStrokeEnd,     // (stroke) => void
  onEraseStrokes,  // (ids[]) => void     eraser removes whole strokes it touches
  onElementCreate, // (element) => void
  onElementCommit, // (element) => void   fires when drag/edit ends
  onElementDelete, // (id) => void         fires when a prop's close button is clicked
  onCursorMove,    // (worldPoint) => void  optional throttled
  peerCursors = [],
  gridOn = true,
  overlaySvg = null, // extra <g> rendered on top (used for geometry tools)
  onContainerRef,    // (ref) => void
  theme = "light",   // "light" | "dark" — controls canvas + grid colours
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  useEffect(() => { if (onContainerRef) onContainerRef(containerRef); }, [onContainerRef]);
  const [drawingPoints, setDrawingPoints] = useState([]); // freehand in world coords
  const [shapeDraft, setShapeDraft] = useState(null);     // {type, x, y, width, height, endX, endY}
  const [drag, setDrag] = useState(null);                 // element drag state
  const [resizeDrag, setResizeDrag] = useState(null);     // {id, startW, startH, startPtr}
  const [panDrag, setPanDrag] = useState(null);           // {startX, startY, panX, panY}
  const [erasing, setErasing] = useState(false);          // eraser pointer-down
  const [space, setSpace] = useState(false);
  const [pinch, setPinch] = useState(null);               // {dist, zoom, panX, panY, cx, cy}

  // Space bar = temp pan. Ignore when the user is typing in any input,
  // textarea or contenteditable — otherwise spacebar is swallowed inside
  // the AI Image modal / title bar / etc.
  useEffect(() => {
    const isTypingTarget = (target) => {
      if (!target) return false;
      const tag = (target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (target.isContentEditable) return true;
      return false;
    };
    const kd = (e) => {
      if (e.code !== "Space") return;
      if (editing || isTypingTarget(e.target)) return;
      e.preventDefault(); setSpace(true);
    };
    const ku = (e) => {
      if (e.code !== "Space") return;
      if (isTypingTarget(e.target)) return;
      setSpace(false);
    };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, [editing]);

  // Zoom via ctrl+wheel or pinch
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = -e.deltaY;
      setView((v) => {
        const nextZoom = Math.max(0.25, Math.min(3, v.zoom * (1 + delta * 0.001)));
        // Zoom toward cursor
        const r = svgRef.current.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width;
        const ny = (e.clientY - r.top) / r.height;
        const worldX = v.panX + nx * (CANVAS_W / v.zoom);
        const worldY = v.panY + ny * (CANVAS_H / v.zoom);
        const newPanX = worldX - nx * (CANVAS_W / nextZoom);
        const newPanY = worldY - ny * (CANVAS_H / nextZoom);
        return { panX: newPanX, panY: newPanY, zoom: nextZoom };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [setView]);

  const isDrawTool = ["pen", "highlighter"].includes(tool);
  const isShapeTool = ["rectangle", "ellipse", "arrow"].includes(tool);
  const isPlaceTool = ["text", "sticky", "math"].includes(tool);

  // Eraser = object eraser: any stroke touched by the pointer is removed.
  const eraseAt = (p) => {
    if (!onEraseStrokes || !strokes.length) return;
    const r = svgRef.current?.getBoundingClientRect();
    const thr = 14 * (CANVAS_W / view.zoom) / (r?.width || CANVAS_W);
    const hits = [];
    for (const s of strokes) {
      if (!s.points?.length || s.tool === "clear" || s.tool === "erase") continue;
      const t = thr + (s.width || 3) / 2;
      let hit = false;
      let prev = null;
      for (const q of s.points) {
        const wp = { x: q.x * CANVAS_W, y: q.y * CANVAS_H };
        if (prev ? distToSeg(p, prev, wp) <= t : Math.hypot(p.x - wp.x, p.y - wp.y) <= t) { hit = true; break; }
        prev = wp;
      }
      if (hit) hits.push(s.id);
    }
    if (hits.length) onEraseStrokes(hits);
  };

  // Custom cursors — SVG data URLs with the "hot-spot" at the actual tip so
  // strokes feel like they're being drawn from the tip of the tool, not the
  // centre of a crosshair. Fixes Ninis follow-up #2 (pen cursor + offset).
  // The trailing "0 24" (or similar) tells the browser which pixel of the
  // cursor image is the click point.
  const PEN_CURSOR = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'><path d='M2 26 L6 22 L18 10 L22 14 L10 26 Z' fill='%23FFFFFF' stroke='%230A0C10' stroke-width='1.5' stroke-linejoin='round'/><path d='M18 10 L22 6 L26 10 L22 14 Z' fill='%23F472B6' stroke='%230A0C10' stroke-width='1.5' stroke-linejoin='round'/><circle cx='3' cy='25' r='1.5' fill='%230A0C10'/></svg>\") 2 26";
  const HIGHLIGHTER_CURSOR = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'><path d='M4 24 L8 20 L20 8 L24 12 L12 24 Z' fill='%23FFD600' stroke='%230A0C10' stroke-width='1.5'/><rect x='2' y='22' width='8' height='4' fill='%230A0C10' rx='1'/></svg>\") 3 25";
  const ERASER_CURSOR = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'><rect x='4' y='10' width='18' height='10' rx='2' fill='%23F472B6' stroke='%230A0C10' stroke-width='1.5'/><rect x='4' y='16' width='18' height='4' rx='1' fill='%23EC4899' stroke='%230A0C10' stroke-width='1.5'/></svg>\") 4 20";

  const svgCursor = space || tool === "pan" ? (panDrag ? "grabbing" : "grab")
    : tool === "pen" ? `${PEN_CURSOR}, crosshair`
    : tool === "highlighter" ? `${HIGHLIGHTER_CURSOR}, crosshair`
    : tool === "eraser" ? `${ERASER_CURSOR}, crosshair`
    : isShapeTool ? "crosshair"
    : isPlaceTool ? "cell"
    : "default";

  // ── Mouse handlers ──
  const onCanvasDown = (e) => {
    if (editing) return;
    // Two-finger touch → pinch-zoom (takes priority over drawing/panning)
    if (e.touches && e.touches.length === 2) {
      const t1 = e.touches[0], t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      setPinch({ dist, zoom: view.zoom, panX: view.panX, panY: view.panY, cx: (t1.clientX + t2.clientX) / 2, cy: (t1.clientY + t2.clientY) / 2 });
      // cancel any freehand draft that may have started with the first touch
      setDrawingPoints([]);
      setShapeDraft(null);
      return;
    }
    const p = clientToWorld(e, svgRef.current, view);
    if (space || tool === "pan") {
      setPanDrag({ startClientX: e.clientX ?? e.touches?.[0]?.clientX, startClientY: e.clientY ?? e.touches?.[0]?.clientY, panX: view.panX, panY: view.panY });
      return;
    }
    if (e.target === svgRef.current || e.target.dataset?.role === "grid") {
      setSelectedId("");
    }
    if (tool === "eraser") {
      setErasing(true);
      eraseAt(p);
      return;
    }
    if (isDrawTool) {
      setDrawingPoints([p]);
      return;
    }
    if (isShapeTool) {
      setShapeDraft({ type: tool, x: p.x, y: p.y, width: 0, height: 0, endX: p.x, endY: p.y });
      return;
    }
    if (isPlaceTool) {
      onElementCreate({ tool, x: p.x, y: p.y });
      return;
    }
  };

  const onCanvasMove = (e) => {
    // Pinch-zoom (2 touches) — update zoom based on distance change
    if (pinch && e.touches && e.touches.length === 2) {
      const t1 = e.touches[0], t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      const scale = dist / (pinch.dist || 1);
      const nextZoom = Math.max(0.25, Math.min(3, pinch.zoom * scale));
      const r = svgRef.current.getBoundingClientRect();
      const nx = (pinch.cx - r.left) / r.width;
      const ny = (pinch.cy - r.top) / r.height;
      const worldX = pinch.panX + nx * (CANVAS_W / pinch.zoom);
      const worldY = pinch.panY + ny * (CANVAS_H / pinch.zoom);
      const newPanX = worldX - nx * (CANVAS_W / nextZoom);
      const newPanY = worldY - ny * (CANVAS_H / nextZoom);
      setView({ panX: newPanX, panY: newPanY, zoom: nextZoom });
      return;
    }
    if (panDrag) {
      const cx = e.clientX ?? e.touches?.[0]?.clientX;
      const cy = e.clientY ?? e.touches?.[0]?.clientY;
      const dx = (cx - panDrag.startClientX) / (svgRef.current.getBoundingClientRect().width) * (CANVAS_W / view.zoom);
      const dy = (cy - panDrag.startClientY) / (svgRef.current.getBoundingClientRect().height) * (CANVAS_H / view.zoom);
      setView((v) => ({ ...v, panX: panDrag.panX - dx, panY: panDrag.panY - dy }));
      return;
    }
    if (erasing) {
      eraseAt(clientToWorld(e, svgRef.current, view));
      return;
    }
    if (drawingPoints.length > 0) {
      const p = clientToWorld(e, svgRef.current, view);
      setDrawingPoints((pts) => [...pts, p]);
      return;
    }
    if (shapeDraft) {
      const p = clientToWorld(e, svgRef.current, view);
      setShapeDraft((d) => ({
        ...d,
        width: p.x - d.x,
        height: p.y - d.y,
        endX: p.x,
        endY: p.y,
      }));
      return;
    }
    if (drag) {
      const p = clientToWorld(e, svgRef.current, view);
      setElements((els) => els.map((el) => {
        if (el.id !== drag.id) return el;
        const dx = p.x - drag.dx - el.x;
        const dy = p.y - drag.dy - el.y;
        if (el.type === "arrow") {
          return { ...el, x: p.x - drag.dx, y: p.y - drag.dy, endX: (el.endX || 0) + dx, endY: (el.endY || 0) + dy };
        }
        return { ...el, x: p.x - drag.dx, y: p.y - drag.dy };
      }));
      return;
    }
    if (resizeDrag) {
      const p = clientToWorld(e, svgRef.current, view);
      setElements((els) => els.map((el) => {
        if (el.id !== resizeDrag.id) return el;
        if (el.type === "arrow" && resizeDrag.endpoint) {
          return resizeDrag.endpoint === "start"
            ? { ...el, x: p.x, y: p.y }
            : { ...el, endX: p.x, endY: p.y };
        }
        if (el.type === "text") {
          const fs = Math.max(14, Math.min(200, Math.round((p.y - el.y) / 1.4)));
          return { ...el, fontSize: fs, width: Math.max(60, p.x - el.x) };
        }
        const next = { ...el, width: Math.max(40, p.x - el.x), height: Math.max(30, p.y - el.y) };
        if (el.type === "math") next.fontSize = Math.max(14, Math.min(120, Math.round(next.height * 0.4)));
        return next;
      }));
      return;
    }
    // Cursor tick — throttle in parent
    if (onCursorMove) {
      const p = clientToWorld(e, svgRef.current, view);
      onCursorMove(p);
    }
  };

  const onCanvasUp = (e) => {
    if (pinch) {
      // Only clear when all touches lifted (or only one remains)
      if (!e || !e.touches || e.touches.length < 2) setPinch(null);
      return;
    }
    if (panDrag) { setPanDrag(null); return; }
    if (erasing) { setErasing(false); return; }
    if (drawingPoints.length > 1) {
      // Emit stroke — normalise to CANVAS_W/H for storage
      const stroke = {
        color,
        width: tool === "highlighter" ? Math.max(strokeWidth, 12) : strokeWidth,
        tool,
        points: drawingPoints.map((p) => ({ x: +(p.x / CANVAS_W).toFixed(4), y: +(p.y / CANVAS_H).toFixed(4) })),
      };
      onStrokeEnd(stroke);
    }
    setDrawingPoints([]);
    if (shapeDraft) {
      const w = shapeDraft.width;
      const h = shapeDraft.height;
      if (Math.abs(w) < 10 && Math.abs(h) < 10 && shapeDraft.type !== "arrow") {
        setShapeDraft(null);
      } else {
        const el = normaliseShape(shapeDraft, color);
        onElementCreate({ tool: "shape-direct", element: el });
        setShapeDraft(null);
      }
    }
    if (drag) {
      const el = elements.find((x) => x.id === drag.id);
      if (el && onElementCommit) onElementCommit(el);
      setDrag(null);
    }
    if (resizeDrag) {
      const el = elements.find((x) => x.id === resizeDrag.id);
      if (el && onElementCommit) onElementCommit(el);
      setResizeDrag(null);
    }
  };

  // ── Element interaction ──
  const onElementDown = (e, el) => {
    // Hand (pan) tool also drags individual elements — panning only happens
    // when the pointer goes down on empty canvas.
    if (space || tool === "eraser" || isDrawTool || isShapeTool || isPlaceTool) return;
    e.stopPropagation();
    setSelectedId(el.id);
    const p = clientToWorld(e, svgRef.current, view);
    setDrag({ id: el.id, dx: p.x - el.x, dy: p.y - el.y });
  };

  const commitLabel = (elId, patch) => {
    let updated;
    setElements((els) => els.map((e) => {
      if (e.id !== elId) return e;
      updated = { ...e, ...patch };
      return updated;
    }));
    setEditing(null);
    if (updated && onElementCommit) onElementCommit(updated);
  };

  // ── ViewBox slice ──
  const vbW = CANVAS_W / view.zoom;
  const vbH = CANVAS_H / view.zoom;
  const viewBox = `${view.panX} ${view.panY} ${vbW} ${vbH}`;

  const isDark = theme === "dark";
  const bg = isDark ? "#0F1218" : "#FBFAF7";
  const gridDot = isDark ? "rgba(255,255,255,0.14)" : "rgba(10,12,16,0.16)";

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden"
      style={{ background: bg }}
      data-testid="wb-canvas-container"
      data-theme={theme}
    >
      <svg
        ref={svgRef}
        viewBox={viewBox}
        preserveAspectRatio="none"
        className="w-full h-full touch-none"
        style={{ cursor: svgCursor }}
        onMouseDown={onCanvasDown}
        onMouseMove={onCanvasMove}
        onMouseUp={onCanvasUp}
        onMouseLeave={onCanvasUp}
        onTouchStart={onCanvasDown}
        onTouchMove={onCanvasMove}
        onTouchEnd={onCanvasUp}
        data-testid="wb-svg"
      >
        <defs>
          <marker id="ah" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
          </marker>
          {gridOn && (
            <pattern id="wbgrid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.8" fill={gridDot} />
            </pattern>
          )}
        </defs>
        {gridOn && (
          <rect
            data-role="grid"
            x={view.panX - vbW} y={view.panY - vbH}
            width={vbW * 3} height={vbH * 3}
            fill="url(#wbgrid)"
          />
        )}

        {/* Elements (props like Urdu worksheet, geometry tools, sims) rendered
            BEFORE strokes so freehand annotations always sit ON TOP. Without
            this order, worksheets would occlude pen marks. */}
        {elements.map((el) => {
          if (el.type === "prop") {
            return renderProp(el, {
              selected: el.id === selectedId,
              onElementDown,
              onResizeStart: (elem, e) => {
                setResizeDrag({ id: elem.id });
                setSelectedId(elem.id);
              },
              onStateChange: (id, patch) => {
                setElements((els) => els.map((x) => x.id === id ? { ...x, state: { ...(x.state || {}), ...patch } } : x));
                const updated = elements.find((x) => x.id === id);
                if (updated && onElementCommit) onElementCommit({ ...updated, state: { ...(updated.state || {}), ...patch } });
              },
              onDelete: (id) => {
                setElements((els) => els.filter((x) => x.id !== id));
                if (id === selectedId) setSelectedId("");
                if (onElementDelete) onElementDelete(id);
              },
            });
          }
          return renderElement(el, {
            selected: el.id === selectedId,
            editing,
            setEditing,
            onElementDown,
            commitLabel,
          });
        })}

        {/* Per-element delete "×" — visible for the currently selected element
            (excluding "prop" elements which have their own inline close btn).
            Fixes Ninis #11 — every element needs a remove control. */}
        {(() => {
          const sel = elements.find((x) => x.id === selectedId);
          if (!sel || sel.type === "prop") return null;
          const bbox = elementBBox(sel);
          if (!bbox) return null;
          const size = 20 / view.zoom;
          const cx = bbox.x + bbox.width + size / 2 + 2 / view.zoom;
          const cy = bbox.y - size / 2 - 2 / view.zoom;
          return (
            <g
              key="wb-selected-delete"
              onMouseDown={(e) => { e.stopPropagation(); }}
              onClick={(e) => {
                e.stopPropagation();
                setElements((els) => els.filter((x) => x.id !== sel.id));
                setSelectedId("");
                if (onElementDelete) onElementDelete(sel.id);
              }}
              style={{ cursor: "pointer" }}
              data-testid="wb-element-delete"
            >
              <circle cx={cx} cy={cy} r={size / 2} fill="#EF4444" stroke="#fff" strokeWidth={1.5 / view.zoom} />
              <line x1={cx - size / 5} y1={cy - size / 5} x2={cx + size / 5} y2={cy + size / 5} stroke="#fff" strokeWidth={2 / view.zoom} strokeLinecap="round" />
              <line x1={cx - size / 5} y1={cy + size / 5} x2={cx + size / 5} y2={cy - size / 5} stroke="#fff" strokeWidth={2 / view.zoom} strokeLinecap="round" />
            </g>
          );
        })()}

        {/* Per-element RESIZE grip — bottom-right of the selected element.
            Enables resizing images (incl. AI-generated), shapes and stickies. */}
        {/* Arrow endpoint grips — drag either end to resize/re-aim the arrow */}
        {(() => {
          const sel = elements.find((x) => x.id === selectedId);
          if (!sel || sel.type !== "arrow") return null;
          const size = 16 / view.zoom;
          return [
            ["start", sel.x, sel.y],
            ["end", sel.endX ?? sel.x + 100, sel.endY ?? sel.y],
          ].map(([ep, cx, cy]) => (
            <circle
              key={`arrow-grip-${ep}`}
              cx={cx} cy={cy} r={size / 2}
              fill="#6366F1" stroke="#fff" strokeWidth={1.5 / view.zoom}
              style={{ cursor: "move" }}
              data-testid={`wb-arrow-grip-${ep}`}
              onMouseDown={(e) => { e.stopPropagation(); setResizeDrag({ id: sel.id, endpoint: ep }); }}
              onTouchStart={(e) => { e.stopPropagation(); setResizeDrag({ id: sel.id, endpoint: ep }); }}
            />
          ));
        })()}

        {(() => {
          const sel = elements.find((x) => x.id === selectedId);
          if (!sel || sel.type === "prop" || sel.type === "arrow") return null;
          const bbox = elementBBox(sel);
          if (!bbox) return null;
          const size = 20 / view.zoom;
          const cx = bbox.x + bbox.width + size / 2 + 2 / view.zoom;
          const cy = bbox.y + bbox.height + size / 2 + 2 / view.zoom;
          return (
            <g
              key="wb-selected-resize"
              onMouseDown={(e) => { e.stopPropagation(); setResizeDrag({ id: sel.id }); }}
              onTouchStart={(e) => { e.stopPropagation(); setResizeDrag({ id: sel.id }); }}
              style={{ cursor: "nwse-resize" }}
              data-testid="wb-element-resize"
            >
              <circle cx={cx} cy={cy} r={size / 2} fill="#6366F1" stroke="#fff" strokeWidth={1.5 / view.zoom} />
              <path
                d={`M ${cx - size / 5} ${cy + size / 6} L ${cx + size / 6} ${cy - size / 5} M ${cx - size / 14} ${cy + size / 5} L ${cx + size / 5} ${cy - size / 14}`}
                stroke="#fff" strokeWidth={1.6 / view.zoom} strokeLinecap="round"
              />
            </g>
          );
        })()}
        {strokes.map((s) => renderStroke(s))}

        {/* Live freehand preview */}
        {drawingPoints.length > 0 && (
          <path
            d={pathFromPoints(drawingPoints)}
            fill="none"
            stroke={color}
            strokeWidth={tool === "highlighter" ? Math.max(strokeWidth, 12) : strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={tool === "highlighter" ? 0.32 : 1}
          />
        )}

        {/* Shape draft preview */}
        {shapeDraft && renderShapeDraft(shapeDraft, color)}

        {/* Peer cursors */}
        {peerCursors.map((c, i) => renderPeerCursor(c, i))}

        {/* Overlay (geometry tools etc.) */}
        {overlaySvg}
      </svg>
    </div>
  );
}

function pathFromPoints(pts) {
  return pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
}

// Distance from point p to segment a–b (world coords)
function distToSeg(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (!len2) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

// Rough bounding box for an element in world coords — used to anchor the
// per-element delete "×" button. Returns { x, y, width, height } or null.
function elementBBox(el) {
  if (!el) return null;
  if (el.type === "arrow") {
    const x1 = Math.min(el.x, el.endX ?? el.x + 100);
    const y1 = Math.min(el.y, el.endY ?? el.y);
    const x2 = Math.max(el.x, el.endX ?? el.x + 100);
    const y2 = Math.max(el.y, el.endY ?? el.y);
    return { x: x1, y: y1, width: Math.max(20, x2 - x1), height: Math.max(20, y2 - y1) };
  }
  if (el.type === "text") {
    const fs = el.fontSize || 26;
    return { x: el.x, y: el.y, width: el.width || Math.max(120, (el.text || "").length * fs * 0.6), height: el.height || fs * 1.4 };
  }
  // rectangle, ellipse, sticky, image, math — all use x/y/width/height
  return { x: el.x, y: el.y, width: el.width || 100, height: el.height || 40 };
}

function renderStroke(s) {
  if (s.tool === "clear" || !s.points?.length) return null;
  const d = s.points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x * CANVAS_W} ${p.y * CANVAS_H}`).join(" ");
  const opacity = s.tool === "highlighter" ? 0.32 : 1;
  return (
    <path
      key={s.id}
      d={d}
      stroke={s.color}
      strokeWidth={s.width}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      opacity={opacity}
    />
  );
}

function normaliseShape(draft, color) {
  const x = Math.min(draft.x, draft.endX);
  const y = Math.min(draft.y, draft.endY);
  const width = Math.abs(draft.width);
  const height = Math.abs(draft.height);
  if (draft.type === "arrow") {
    return { id: Math.random().toString(36).slice(2, 10), type: "arrow", x: draft.x, y: draft.y, endX: draft.endX, endY: draft.endY, label: "", color };
  }
  return {
    id: Math.random().toString(36).slice(2, 10),
    type: draft.type,
    x, y,
    width: Math.max(40, width),
    height: Math.max(28, height),
    label: "",
    color,
    fontSize: 18,
  };
}

function renderShapeDraft(d, color) {
  if (d.type === "rectangle") {
    return <rect x={Math.min(d.x, d.endX)} y={Math.min(d.y, d.endY)} width={Math.abs(d.width)} height={Math.abs(d.height)} rx="10" fill="rgba(255,214,0,0.06)" stroke={color} strokeDasharray="6 4" strokeWidth="2" />;
  }
  if (d.type === "ellipse") {
    const cx = (d.x + d.endX) / 2;
    const cy = (d.y + d.endY) / 2;
    return <ellipse cx={cx} cy={cy} rx={Math.abs(d.width) / 2} ry={Math.abs(d.height) / 2} fill="rgba(255,214,0,0.06)" stroke={color} strokeDasharray="6 4" strokeWidth="2" />;
  }
  if (d.type === "arrow") {
    return <line x1={d.x} y1={d.y} x2={d.endX} y2={d.endY} stroke={color} strokeWidth="3" strokeDasharray="6 4" markerEnd="url(#ah)" />;
  }
  return null;
}

function renderPeerCursor(c, i) {
  const cx = c.x * CANVAS_W;
  const cy = c.y * CANVAS_H;
  const fill = c.user_role === "teacher" ? "#FFD600" : "#6366F1";
  const label = (c.user_name || "?").slice(0, 18);
  return (
    <g key={(c.user_id || "u") + i} pointerEvents="none">
      <circle cx={cx} cy={cy} r="6" fill={fill} stroke="#fff" strokeWidth="2" />
      <rect x={cx + 10} y={cy - 8} rx="3" ry="3" width={label.length * 6 + 12} height="16" fill="rgba(10,12,16,0.85)" />
      <text x={cx + 16} y={cy + 3} fill="#fff" fontSize="10" fontFamily="ui-monospace, monospace">{label}</text>
    </g>
  );
}

function renderElement(el, ctx) {
  const { selected, editing, setEditing, onElementDown, commitLabel } = ctx;
  const stroke = selected ? "#FFB400" : el.color || "#0A0C10";
  const strokeW = selected ? 3 : 2;

  if (el.type === "image") {
    return (
      <g key={el.id} onMouseDown={(e) => onElementDown(e, el)} style={{ cursor: "grab" }} data-testid={`wb-el-${el.id}`}>
        <image href={el.src} x={el.x} y={el.y} width={el.width} height={el.height} preserveAspectRatio="xMidYMid meet" />
        <rect x={el.x} y={el.y} width={el.width} height={el.height} fill="none" stroke={selected ? "#FFB400" : "transparent"} strokeWidth={selected ? 3 : 0} pointerEvents="none" />
      </g>
    );
  }
  if (el.type === "rectangle" || el.type === "ellipse") {
    const isRect = el.type === "rectangle";
    const shape = isRect
      ? <rect x={el.x} y={el.y} width={el.width} height={el.height} rx="10" fill="none" stroke={stroke} strokeWidth={strokeW} />
      : <ellipse cx={el.x + el.width / 2} cy={el.y + el.height / 2} rx={el.width / 2} ry={el.height / 2} fill="none" stroke={stroke} strokeWidth={strokeW} />;
    const hasLabel = !!(el.label && String(el.label).trim());
    return (
      <g key={el.id} onMouseDown={(e) => onElementDown(e, el)} data-testid={`wb-el-${el.id}`} style={{ cursor: "grab" }}>
        {/* Transparent hit-catcher so the shape is easy to grab even with no fill */}
        {isRect ? (
          <rect x={el.x} y={el.y} width={el.width} height={el.height} rx="10" fill="transparent" pointerEvents="all" />
        ) : (
          <ellipse cx={el.x + el.width / 2} cy={el.y + el.height / 2} rx={el.width / 2} ry={el.height / 2} fill="transparent" pointerEvents="all" />
        )}
        {shape}
        {editing?.id === el.id ? (
          <foreignObject x={el.x + 4} y={el.y + el.height / 2 - 18} width={el.width - 8} height={40}>
            <input autoFocus defaultValue={el.label || ""}
              onBlur={(e) => commitLabel(el.id, { label: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              className="w-full text-center text-base bg-transparent outline-none border-b border-[#0A0C10]/30"
              placeholder="Label (optional)"
            />
          </foreignObject>
        ) : hasLabel && (el.label || "").includes("$") ? (
          <foreignObject x={el.x + 4} y={el.y + 4} width={el.width - 8} height={el.height - 8}
            onDoubleClick={(e) => { e.stopPropagation(); setEditing({ id: el.id, kind: "label" }); }}
            style={{ cursor: "grab" }}
          >
            <div className="w-full h-full flex items-center justify-center text-center px-1" style={{ color: el.color, fontSize: (el.fontSize || 18) + "px" }}>
              <MathText text={el.label} />
            </div>
          </foreignObject>
        ) : hasLabel ? (
          <text
            x={el.x + el.width / 2}
            y={el.y + el.height / 2 + 6}
            textAnchor="middle"
            fontSize={el.fontSize || 18}
            fill={el.color || "#0A0C10"}
            onDoubleClick={(e) => { e.stopPropagation(); setEditing({ id: el.id, kind: "label" }); }}
            style={{ userSelect: "none", cursor: "grab" }}
          >{el.label}</text>
        ) : (
          /* No label — still allow double-click on the shape to add one */
          <rect x={el.x} y={el.y} width={el.width} height={el.height} fill="transparent" pointerEvents="all"
            onDoubleClick={(e) => { e.stopPropagation(); setEditing({ id: el.id, kind: "label" }); }} />
        )}
      </g>
    );
  }
  if (el.type === "text") {
    return editing?.id === el.id ? (
      <foreignObject key={el.id} x={el.x} y={el.y} width={Math.max(420, el.width || 420)} height={Math.max(160, el.height || 160)}>
        <textarea autoFocus defaultValue={el.text || ""}
          placeholder="Type your text… (Esc to finish)"
          onBlur={(e) => commitLabel(el.id, { text: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Escape") e.target.blur(); }}
          style={{ fontSize: (el.fontSize || 24) + "px", lineHeight: 1.35, color: el.color || "#0A0C10" }}
          className="w-full h-full bg-white/70 rounded-md p-2 outline-none border border-dashed border-[#0A0C10]/30 resize-none font-medium"
        />
      </foreignObject>
    ) : (
      // foreignObject render for ALL text — supports multi-line sentences,
      // word-wrap within the box, and $math$ snippets.
      <foreignObject key={el.id} x={el.x} y={el.y} width={Math.max(420, el.width || 420)} height={Math.max(160, el.height || 160)}
        onDoubleClick={(e) => { e.stopPropagation(); setEditing({ id: el.id, kind: "text" }); }}
        onMouseDown={(e) => onElementDown(e, el)} style={{ cursor: "grab" }}
        data-testid={`wb-el-${el.id}`}
      >
        <div style={{
          fontSize: (el.fontSize || 24) + "px",
          color: selected ? "#FFB400" : (el.color || "#0A0C10"),
          fontWeight: 500, lineHeight: 1.35,
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {(el.text || "").trim()
            ? ((el.text || "").includes("$") ? <MathText text={el.text} /> : el.text)
            : <span style={{ opacity: 0.35, fontStyle: "italic" }}>Double-click to type…</span>}
        </div>
      </foreignObject>
    );
  }
  if (el.type === "sticky") {
    return (
      <g key={el.id} onMouseDown={(e) => onElementDown(e, el)} data-testid={`wb-el-${el.id}`} style={{ cursor: "grab" }}>
        <rect x={el.x + 3} y={el.y + 4} width={el.width} height={el.height} fill="rgba(0,0,0,0.12)" />
        <rect x={el.x} y={el.y} width={el.width} height={el.height} fill={el.color || "#FEF08A"} stroke={selected ? "#FFB400" : "rgba(0,0,0,0.15)"} strokeWidth={selected ? 3 : 1} />
        {editing?.id === el.id ? (
          <foreignObject x={el.x + 8} y={el.y + 8} width={el.width - 16} height={el.height - 16}>
            <textarea autoFocus defaultValue={el.label || ""}
              onBlur={(e) => commitLabel(el.id, { label: e.target.value })}
              className="w-full h-full text-sm bg-transparent outline-none resize-none font-medium"
            />
          </foreignObject>
        ) : (
          <foreignObject x={el.x + 8} y={el.y + 8} width={el.width - 16} height={el.height - 16}
            onDoubleClick={(e) => { e.stopPropagation(); setEditing({ id: el.id, kind: "label" }); }}
          >
            <div className="w-full h-full text-sm font-medium text-[#0A0C10] whitespace-pre-wrap overflow-hidden">
              {(el.label || "").includes("$") ? <MathText text={el.label} /> : (el.label || "Note…")}
            </div>
          </foreignObject>
        )}
      </g>
    );
  }
  if (el.type === "math") {
    return editing?.id === el.id ? (
      <foreignObject key={el.id} x={el.x} y={el.y} width={Math.max(420, el.width || 420)} height={100}>
        <input autoFocus defaultValue={el.text || ""}
          onBlur={(e) => commitLabel(el.id, { text: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          placeholder="$E = mc^2$"
          className="w-full text-lg bg-white outline-none border border-[#0A0C10]/30 rounded-md px-3 py-2 font-mono"
        />
      </foreignObject>
    ) : (
      <foreignObject key={el.id} x={el.x} y={el.y} width={Math.max(420, el.width || 500)} height={Math.max(90, el.height || 90)}
        onMouseDown={(e) => onElementDown(e, el)}
        onDoubleClick={(e) => { e.stopPropagation(); setEditing({ id: el.id, kind: "text" }); }}
        style={{ cursor: "grab" }}
        data-testid={`wb-el-${el.id}`}
      >
        <div className={`w-full h-full flex items-center px-2 ${selected ? "ring-2 ring-[#FFB400]" : ""}`} style={{ fontSize: (el.fontSize || 24) + "px" }}>
          <MathText text={el.text || "$E = mc^2$"} />
        </div>
      </foreignObject>
    );
  }
  if (el.type === "arrow") {
    return (
      <g key={el.id} onMouseDown={(e) => onElementDown(e, el)} style={{ cursor: "grab", color: stroke }} data-testid={`wb-el-${el.id}`}>
        <line x1={el.x} y1={el.y} x2={el.endX ?? (el.x + 100)} y2={el.endY ?? el.y} stroke={stroke} strokeWidth={strokeW} markerEnd="url(#ah)" />
        {el.label && (
          <text x={(el.x + (el.endX ?? el.x + 100)) / 2} y={((el.y + (el.endY ?? el.y)) / 2) - 8} textAnchor="middle" fontSize={14} fill={el.color || "#0A0C10"}>{el.label}</text>
        )}
      </g>
    );
  }
  return null;
}
